import angular from "angular";

// MODULE DEPENDENCIES
import "angular-animate";
import "angular-cookies";
import "angular-sanitize";
import "angular-messages";
import "angular-local-storage";
import "angular-uuid";
import "angular-ui-notification";
import "@uirouter/angularjs";
import "@uirouter/angularjs/release/stateEvents";
import "angular-ui-validate";
import "angular-ui-bootstrap";
import "ui-select";
import "angular-moment";
import "angular-dynamic-locale";
import "ngclipboard";
import "angular-ui-tinymce";
import "angular-youtube-embed";
import "ng-material-floating-button/src/mfb-directive";
import "ng-infinite-scroll";
import "ng-notifications-bar";
import "ng-file-upload";

// MODULES
import AuthModule from "./components/auth/auth.module";
import CreateOrganizationModule from "./components/create-organization/create-organization.module";
import DashboardModule from "./components/dashboard/dashboard.module";
import LayoutModule from "./components/layout/layout.module";
import LibraryCostsModule from "./components/library-costs/library-costs.module";
import LibraryContactsModule from "./components/library-contacts/library-contacts.module";
import LibraryTariffModule from "./components/library-tariffs/library-tariffs.module";
import OrganizationSettingsModule from "./components/organization-settings/organization-settings.module";
import ProfileModule from "./components/profile/profile.module";
import QuoteEditorModule from "./components/quote-editor/quote-editor.module";
import TemplateSelectorModule from "./components/template-selector/template-selector.module";

// CONSTANTS
import appLanguages from "./config/language/appLanguages";
import countries from "./config/countries";
import currencies from "./config/currencies";
import fonts from "./config/fonts";
import quoteLanguages from "./config/language/quoteLanguages";
import moment from "moment-timezone";

// VALUES
import errorMessages from "./config/errorMessages";

// CONFIG
import angularConfig from "./angularConfig";
import angularRoutes from "./angularRoutes";

// RUN
import angularRun from "./angularRun";
import angularTemplateCache from "./angularTemplateCache";

// FACTORIES
import AuthenticationInterceptor from "./factories/interceptors/AuthenticationInterceptor";
import ErrorInterceptor from "./factories/interceptors/ErrorInterceptor";
import ImpersonateInterceptor from "./factories/interceptors/ImpersonateInterceptor";
import UnauthorizedInterceptor from "./factories/interceptors/UnauthorizedInterceptor";
import ContactFactory from "./factories/Contact";
import CostFactory from "./factories/Cost";
import CostFolderFactory from "./factories/CostFolder";
import CostSnapshotFactory from "./factories/CostSnapshot";
import EmailFactory from "./factories/Email";
import InvitationFactory from "./factories/Invitation";
import OrganizationFactory from "./factories/Organization";
import PhotoFactory from "./factories/Photo";
import PricingTableSnapshotFactory from "./factories/PricingTableSnapshot";
import QuoteFactory from "./factories/Quote";
import QuoteLineFactory from "./factories/QuoteLine";
import QuoteSectionTemplateFactory from "./factories/QuoteSectionTemplate";
import QuoteSectionSnapshotFactory from "./factories/QuoteSectionSnapshot";
import QuoteTemplateFactory from "./factories/QuoteTemplate";
import QuoteVersionFactory from "./factories/QuoteVersion";
import TariffFactory from "./factories/Tariff";
import TaxFactory from "./factories/Tax";
import UserFactory from "./factories/User";

// SERVICES
import FilePicker from "./services/FilePicker";
import ModalsManagerService from "./services/ModalsManager";
import LocaleService from "./services/LocaleService";
import OrganizationService from "./services/OrganizationService";
import SecurityManagerService from "./services/SecurityManager";
import SweetAlertService from "./services/SweetAlertService";
import TinyMceService from "./services/TinyMceService";
import UtilsService from "./services/Utils";

// DIRECTIVES
import bootstrapTooltip from "./directives/bootstrap-tooltip";
import btnLoading from "./directives/btn-loading";
import dateValidator from "./directives/date-validator";
import pickr from "./directives/pickr";
import fgLine from "./directives/fg-line";
import passwordCheck from "./directives/password-check";
import profilePicture from "./directives/profile-picture";
import quoteStyles from "./directives/quote-styles";
import toggleSubmenu from "./directives/toggle-submenu";
import uniqueSlug from "./directives/unique-slug";
import validateSome from "./directives/validate-some";
import validateDomain from "./directives/validate-domain";

// FILTERS
import canonize from "./filters/canonize";
import countryCodeToName from "./filters/countryCodeToName";
import currencyFormatter from "./filters/currencyFormatter";
import currencySymbol from "./filters/currencySymbol";
import customHighLight from "./filters/customHighlight";
import ellipsis from "./filters/ellipsis";
import firstLetter from "./filters/firstLetter";
import lineBreaksToBrTag from "./filters/lineBreaksToBrTag";
import padStart from "./filters/padStart";
import reverse from "./filters/reverse";
import trans from "./filters/trans";

angular
  .module("ProposalSoftware", [
    "LocalStorageModule",
    "ngAnimate",
    "ngCookies",
    "ngNotificationsBar",
    "ngSanitize",
    "ngMessages",
    "ui-notification",
    "ui.router",
    "ui.router.state.events",
    "ui.validate",
    "ui.bootstrap",
    "ui.select",
    "angularMoment",
    "ngclipboard",
    "angular-uuid",
    "ui.tinymce",
    "youtube-embed",
    "ng-mfb",
    "infinite-scroll",
    "ngFileUpload",
    "tmh.dynamicLocale",
    AuthModule.name,
    CreateOrganizationModule.name,
    LayoutModule.name,
    DashboardModule.name,
    LibraryCostsModule.name,
    LibraryContactsModule.name,
    LibraryTariffModule.name,
    OrganizationSettingsModule.name,
    ProfileModule.name,
    QuoteEditorModule.name,
    TemplateSelectorModule.name,
  ])
  // CONSTANTS
  .constant("appLanguages", appLanguages)
  .constant("countries", countries)
  .constant("currencies", currencies)
  .constant("fonts", fonts)
  .constant("quoteLanguages", quoteLanguages)
  .constant("moment", moment)
  // VALUES
  .value("errorMessages", errorMessages)
  // CONFIG
  .config(angularConfig)
  .config(angularRoutes)
  // RUN
  .run(angularRun)
  .run(angularTemplateCache)
  // FACTORY
  .factory("AuthenticationInterceptor", AuthenticationInterceptor)
  .factory("ErrorInterceptor", ErrorInterceptor)
  .factory("ImpersonateInterceptor", ImpersonateInterceptor)
  .factory("UnauthorizedInterceptor", UnauthorizedInterceptor)
  .factory("Contact", ContactFactory)
  .factory("Cost", CostFactory)
  .factory("CostFolder", CostFolderFactory)
  .factory("CostSnapshot", CostSnapshotFactory)
  .factory("Email", EmailFactory)
  .factory("Invitation", InvitationFactory)
  .factory("Organization", OrganizationFactory)
  .factory("Photo", PhotoFactory)
  .factory("PricingTableSnapshot", PricingTableSnapshotFactory)
  .factory("Quote", QuoteFactory)
  .factory("QuoteLine", QuoteLineFactory)
  .factory("QuoteSectionTemplate", QuoteSectionTemplateFactory)
  .factory("QuoteSectionSnapshot", QuoteSectionSnapshotFactory)
  .factory("QuoteTemplate", QuoteTemplateFactory)
  .factory("QuoteVersion", QuoteVersionFactory)
  .factory("Tariff", TariffFactory)
  .factory("Tax", TaxFactory)
  .factory("User", UserFactory)
  // SERVICES
  .service("FilePicker", FilePicker)
  .service("ModalsManager", ModalsManagerService)
  .service("LocaleService", LocaleService)
  .service("OrganizationService", OrganizationService)
  .service("SecurityManager", SecurityManagerService)
  .service("SweetAlertService", SweetAlertService)
  .service("TinyMceService", TinyMceService)
  .service("Utils", UtilsService)
  // DIRECTIVES
  .directive("bootstrapTooltip", bootstrapTooltip)
  .directive("btnLoading", btnLoading)
  .directive("dateValidator", dateValidator)
  .directive("pickr", pickr)
  .directive("fgLine", fgLine)
  .directive("passwordCheck", passwordCheck)
  .directive("profilePicture", profilePicture)
  .directive("quoteStyles", quoteStyles)
  .directive("toggleSubmenu", toggleSubmenu)
  .directive("uniqueSlug", uniqueSlug)
  .directive("validateSome", validateSome)
  .directive("validateDomain", validateDomain)
  // FILTER
  .filter("canonize", canonize)
  .filter("countryCodeToName", countryCodeToName)
  .filter("currencyFormatter", currencyFormatter)
  .filter("currencySymbol", currencySymbol)
  .filter("customHighlight", customHighLight)
  .filter("ellipsis", ellipsis)
  .filter("firstLetter", firstLetter)
  .filter("lineBreaksToBrTag", lineBreaksToBrTag)
  .filter("padStart", padStart)
  .filter("reverse", reverse)
  .filter("trans", trans);

export default angular;
